import CONFIG from '../config/config';
import { instanceWithV2 as instance } from './SimpleAPIClient';
import { serializeParamsForSpringBoot } from './serializeParamsForSpringBoot';
import { CommissionContractTemplate } from '../types/CommissionContractTemplate';
import { CommissionStatus } from '../types/commission-statuses';
import { PaymentStatus } from '../types/payment-statuses';
import { PaymentPlan } from '../types/payment-plan';
import axios from 'axios';

export interface ContractVersion {
  pdf: string;
  createdAt: string;
}

export interface Addon {
  name: string;
  description: string;
  cost: number;
  currency: string;
}

export interface CommissionContract {
  id?: number;
  brideName: string;
  brideSurname: string;
  bridePhone: string;
  brideEmail: string;
  brideSocial: string;
  dress: string;
  dressSize: string;
  purchaseYear: string;
  purchasePriceAmount?: number;
  purchasePriceCurrency?: string;
  desiredSalePriceAmount?: number;
  desiredSalePriceCurrency?: string;
  status: CommissionStatus;
  itemReceived: boolean;
  acceptedDate: string;
  endDate: string;
  payment: PaymentStatus;
  contracts?: ContractVersion[];
  attachments?: string[];
  shareLink?: string;
  trackingId?: string;
  submissionDate?: string;
  addons?: Addon[];
  salonSplitPercentage?: number;
  brideSplitPercentage?: number;
  locale?: string;
  damagesNotes?: string;
  notes?: string;
  paymentPlan?: PaymentPlan;
  requiresPayment?: boolean;
  contractNumber?: string;
  contractReference?: string;
}

export interface CommissionComment {
  id?: number;
  comment: string;
  writtenBy: string;
  creationTime: string;
  commissionContractId: number;
}

export interface PaginatedCommissionResponse {
  content: CommissionContract[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

const ComissionContractsAPI = {
  getAll: () => instance(CONFIG.API_ENDPOINT).get<CommissionContract[]>(`v1/commission-contracts`),
  getById: (id: number) => instance(CONFIG.API_ENDPOINT).get<CommissionContract>(`v1/commission-contracts/${id}`),
  create: (contract: CommissionContract) => instance(CONFIG.API_ENDPOINT).post<CommissionContract>(`v1/commission-contracts`, contract),
  update: (id: number, contract: CommissionContract) => instance(CONFIG.API_ENDPOINT).put<CommissionContract>(`v1/commission-contracts/${id}`, contract),
  getFiltered: (filters: { month?: string | null; payment?: PaymentStatus[] | null; status?: CommissionStatus[] | null; itemReceived?: boolean | null; search?: string | null; page?: number; size?: number; sortBy?: string | null; sortDirection?: 'ASC' | 'DESC' | null }) => {
    const params: Record<string, any> = {};
    if (filters.month !== undefined && filters.month !== null) params.month = filters.month;
    if (filters.payment && filters.payment.length > 0) params.payment = filters.payment;
    if (filters.status && filters.status.length > 0) params.status = filters.status;
    if (filters.itemReceived !== undefined && filters.itemReceived !== null) params.itemReceived = filters.itemReceived;
    if (filters.search !== undefined && filters.search !== null) params.search = filters.search;
    if (filters.page !== undefined) params.page = filters.page;
    if (filters.size !== undefined) params.size = filters.size;
    if (filters.sortBy !== undefined && filters.sortBy !== null) params.sortBy = filters.sortBy;
    if (filters.sortDirection !== undefined && filters.sortDirection !== null) params.sortDirection = filters.sortDirection;
    
    return instance(CONFIG.API_ENDPOINT).get<PaginatedCommissionResponse>(`v1/commission-contracts`, { 
      params,
      paramsSerializer: serializeParamsForSpringBoot,
    });
  },
  getTemplate: (locale: string = "EN_US") => instance(CONFIG.API_ENDPOINT).get<CommissionContractTemplate>(`v1/commission-contracts/templates?locale=${locale}`),
  saveTemplate: (locale: string = "EN_US", contractTemplate: any) => instance(CONFIG.API_ENDPOINT).put(`v1/commission-contracts/templates/${locale}`, contractTemplate),
  updateEnabledStatus: (locale: string = "EN_US", enabled: boolean) => instance(CONFIG.API_ENDPOINT).put(`v1/commission-contracts/templates/${locale}/enabled`, { enabled }),
  getByTrackingId: (trackingId: string) => axios.get<CommissionContract>(`${CONFIG.API_ENDPOINT}/v1/public/commission-contracts/tracking/${trackingId}`),
  delete: (id: number) => instance(CONFIG.API_ENDPOINT).delete(`v1/commission-contracts/${id}`),
  getComments: (commissionContractId: number) => 
    instance(CONFIG.API_ENDPOINT).get<CommissionComment[]>(`v1/commission-contracts/${commissionContractId}/comments`),
  addComment: (commissionContractId: number, comment: { comment: string, writtenBy: string }) =>
    instance(CONFIG.API_ENDPOINT).post<CommissionComment>(`v1/commission-contracts/${commissionContractId}/comments`, comment),
  deleteComment: (commentId: number) =>
    instance(CONFIG.API_ENDPOINT).delete(`v1/commission-contracts/comments/${commentId}`),
};

export default ComissionContractsAPI; 